// Gemini Adapter for Unified LLM Interface

import type { GeminiClient } from '../../client/gemini/index.js'
import type {
  ChatArgs,
  ProviderAdapter,
  UnifiedChatResponse,
  UnifiedMessage,
  UnifiedResponseMessage,
  UnifiedToolCall,
} from './types.js'

interface GeminiConversion {
  contents: unknown[]
  systemInstruction: unknown | null
}

const convertMessagesToGemini = (messages: UnifiedMessage[]): GeminiConversion => {
  const contents: unknown[] = []
  const systemMessages: string[] = []

  for (const msg of messages) {
    if (msg.role === 'system') {
      systemMessages.push(msg.content as string)
    } else if (msg.role === 'user') {
      contents.push({ role: 'user', parts: [{ text: msg.content }] })
    } else if (msg.role === 'assistant') {
      const parts: unknown[] = []
      if (msg.content && msg.content !== 'null') {
        parts.push({ text: msg.content })
      }
      if (msg.toolCalls) {
        msg.toolCalls.forEach(tc => {
          try {
            parts.push({
              functionCall: {
                name: tc.function.name,
                args: JSON.parse(tc.function.arguments as string),
              },
            })
          } catch (e) {
            console.error('[Gemini] Failed to parse tool call arguments:', {
              toolName: tc.function.name,
              arguments: tc.function.arguments,
            })
            throw e
          }
        })
      }
      if (parts.length > 0) {
        contents.push({ role: 'model', parts })
      }
    } else if (msg.role === 'tool') {
      try {
        let parsedContent: unknown = JSON.parse(msg.content as string)
        if (
          typeof parsedContent !== 'object' ||
          parsedContent === null ||
          Array.isArray(parsedContent)
        ) {
          parsedContent = { result: parsedContent }
        }
        contents.push({
          role: 'user',
          parts: [
            {
              functionResponse: {
                name: msg.toolName,
                response: parsedContent,
              },
            },
          ],
        })
      } catch (e) {
        console.error('[Gemini] Failed to parse tool response:', {
          toolName: msg.toolName,
          content: msg.content,
        })
        throw e
      }
    }
  }

  const systemInstruction =
    systemMessages.length > 0 ? { parts: [{ text: systemMessages.join('\n\n') }] } : null

  return { contents, systemInstruction }
}

export function createGeminiAdapter(geminiClient: GeminiClient): ProviderAdapter {
  const chat = async ({
    apiKey,
    model = 'gemini-2.5-flash',
    messages,
    tools,
    config = {},
  }: ChatArgs): Promise<UnifiedChatResponse> => {
    const { contents, systemInstruction } = convertMessagesToGemini(messages)

    const geminiTools = tools
      ? [
          {
            functionDeclarations: tools.map(tool => ({
              name: tool.name,
              description: tool.description,
              parameters: tool.parameters,
            })),
          },
        ]
      : undefined

    const generationConfig: Record<string, unknown> = {}
    if (config.temperature !== undefined) generationConfig.temperature = config.temperature
    if (config.maxTokens !== undefined) generationConfig.maxOutputTokens = config.maxTokens
    if (config.topP !== undefined) generationConfig.topP = config.topP
    if (config.topK !== undefined) generationConfig.topK = config.topK

    const response: any = await geminiClient.generateContent({
      apiKey,
      model,
      contents,
      tools: geminiTools,
      toolConfig: config.toolChoice
        ? { functionCallingConfig: { mode: config.toolChoice } }
        : undefined,
      systemInstruction,
      generationConfig: Object.keys(generationConfig).length > 0 ? generationConfig : undefined,
    })

    if (!response.candidates || response.candidates.length === 0) {
      throw new Error(`No candidates in Gemini response: ${JSON.stringify(response)}`)
    }

    const candidate = response.candidates[0]

    if (!candidate.content || !candidate.content.parts || candidate.content.parts.length === 0) {
      throw new Error(
        `Gemini returned empty response. FinishReason: ${candidate.finishReason}. This usually indicates an issue with the conversation format or tool responses.`,
      )
    }

    const parts = candidate.content.parts

    const result: UnifiedResponseMessage = {
      role: 'assistant',
      finishReason: candidate.finishReason,
      usage: {
        promptTokens: response.usageMetadata?.promptTokenCount,
        completionTokens: response.usageMetadata?.candidatesTokenCount,
        totalTokens: response.usageMetadata?.totalTokenCount,
      },
    }

    const textPart = parts.find((p: any) => p.text)
    if (textPart) {
      result.content = textPart.text
    }

    const functionCalls = parts.filter((p: any) => p.functionCall)
    if (functionCalls.length > 0) {
      result.toolCalls = functionCalls.map(
        (fc: any, index: number): UnifiedToolCall => ({
          id: `call_${Date.now()}_${index}`,
          type: 'function',
          function: {
            name: fc.functionCall.name,
            arguments: JSON.stringify(fc.functionCall.args),
          },
        }),
      )
    }

    return { message: result, raw: response }
  }

  const listModels = async ({ apiKey }: { apiKey?: string }) => {
    return await geminiClient.listModels({ apiKey })
  }

  return { chat, listModels }
}
